import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ALL_LEVELS = [1, 2, 3] as const;

interface ShareLevelPickerProps {
  /** Current selection (1 = friends & family, 2 = friends of friends, 3 = anyone). */
  value: number;
  onChange: (level: number) => void;
  disabled?: boolean;
  /**
   * Locale cap from `Locale.maxShareConnectionLevel`. Options above this
   * are hidden — the BE clamps via `LEAST(listing, locale)` anyway, no
   * reason to offer something the chef can't actually achieve.
   *
   * If the current `value` is above the cap (e.g. chef had "Anyone" set
   * before a locale operator narrowed the cap), the over-cap row is
   * still rendered so the active selection stays visible. Once the chef
   * picks a lower option, the over-cap row disappears from the list.
   *
   * Default 3 = no narrowing (locale max not set yet or locale.maxShareConnectionLevel undefined).
   */
  maxLevel?: number;
  /**
   * i18n key prefix in the `profile` namespace for the level labels, so the
   * same 3-tier picker can back both the outbound "who can see your dishes"
   * card (`reach.*`, the default) and the inbound "whose listings I can see"
   * card (`seeReach.*`) without the option rows drifting apart. Each prefix
   * must define `.level{1,2,3}.title` / `.description`.
   */
  keyPrefix?: string;
}

/**
 * The shared 3-tier audience picker (friends & family / friends of friends /
 * anyone). Used for the per-user outbound default and the first-publish prompt
 * (`reach.*`) and the inbound feed preference (`seeReach.*`), so the option
 * rows + interaction never drift. Copy lives in the `profile` namespace under
 * the `keyPrefix`.
 */
export const ShareLevelPicker: React.FC<ShareLevelPickerProps> = ({
  value,
  onChange,
  disabled,
  maxLevel = 3,
  keyPrefix = 'reach',
}) => {
  const { t } = useTranslation('profile');

  // Strict filter — only offer levels at or below the locale cap. An
  // over-cap saved value is NOT shown as an option (the chef can't
  // re-pick it here; if they want to widen, they'd have to be in a
  // locale that allows it).
  const visibleLevels = ALL_LEVELS.filter((l) => l <= maxLevel);
  // Visual "selected" row uses the clamped value so something always
  // shows as active even when the persisted value exceeds the cap. The
  // store stays at the original `value` until the chef explicitly picks
  // (this is just a display clamp — see the user spec: "shown as
  // selected even though it's not unless they save").
  const activeLevel = Math.min(value, maxLevel);

  return (
    <div className="flex flex-col gap-2">
      {visibleLevels.map((level) => {
        const active = activeLevel === level;
        return (
          <button
            key={level}
            type="button"
            onClick={() => {
              if (!active) onChange(level);
            }}
            disabled={disabled}
            className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors disabled:opacity-50 ${
              active ? 'border-brand-primary' : 'border-border hover:bg-muted'
            }`}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {t(`${keyPrefix}.level${level}.title`)}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(`${keyPrefix}.level${level}.description`)}
              </p>
            </div>
            {active && <Check className="h-4 w-4 shrink-0 text-brand-primary" />}
          </button>
        );
      })}
    </div>
  );
};
